import React, { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axiosInstance";
import { path } from "../../navigation/path";

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<any>({
    user_name: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const handleChange = (key: any, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSave = async () => {
    if (!formData?.user_name) {
      toast.error("Username is required");
      return;
    } else if (!formData?.password) {
      toast.error("Password is required");
      return;
    }
    setLoading(true);
    try {
      const resp: any = await axiosInstance.post(`/api/auth/login`, formData);
      if (resp.status === 200) {
        toast.success(
          resp?.data?.role
            ? "Audit login successful!"
            : "Superadmin login successful!"
        );
        localStorage.setItem("token", resp?.data?.token);
        localStorage.setItem("role", resp?.data?.role ?? "");
        
        // Store role entity information for permission checking
        if (resp?.data?.roleEntity) {
          localStorage.setItem("roleEntity", JSON.stringify(resp.data.roleEntity));
          const perms = resp.data.roleEntity.permissions || [];
          localStorage.setItem("permissions", JSON.stringify(perms));
        } else {
          localStorage.removeItem("roleEntity");
          // Zone admin with no role: allow member list view
          const zoneAdminZoneIds = resp?.data?.zone_admin_zone_ids;
          if (Array.isArray(zoneAdminZoneIds) && zoneAdminZoneIds.length > 0) {
            localStorage.setItem("permissions", JSON.stringify(["member.view"]));
          } else {
            localStorage.removeItem("permissions");
          }
        }
        
        // Store Super Admin flag
        if (resp?.data?.isSuperAdmin) {
          localStorage.setItem("isSuperAdmin", "true");
        } else {
          localStorage.removeItem("isSuperAdmin");
        }
        
        // Store member_id and zone_admin_zone_ids for "My Profile" and zone admin access
        if (resp?.data?.member_id != null) {
          localStorage.setItem("member_id", String(resp.data.member_id));
        } else {
          localStorage.removeItem("member_id");
        }
        if (Array.isArray(resp?.data?.zone_admin_zone_ids)) {
          localStorage.setItem("zone_admin_zone_ids", JSON.stringify(resp.data.zone_admin_zone_ids));
        } else {
          localStorage.removeItem("zone_admin_zone_ids");
        }
        
        // No-role users with member_id: send to My Information so they have a landing page
        const hasMemberId = resp?.data?.member_id != null;
        const hasDashboardAccess = resp?.data?.isSuperAdmin || (resp?.data?.roleEntity?.permissions && (resp.data.roleEntity.permissions.includes("dashboard.view") || resp.data.roleEntity.permissions.includes("admin") || resp.data.roleEntity.permissions.includes("*")));
        if (hasMemberId && !hasDashboardAccess) {
          navigate(path.MY_PROFILE);
        } else {
          navigate("/");
        }
      }
    } catch (Error: any) {
      console.log(Error);
      toast.error(Error?.response?.data?.body || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f5f5",
    padding: "16px",
    position: "relative",
    overflow: "hidden",
  };

  const backgroundBeforeStyle: React.CSSProperties = {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 30%, rgba(0, 0, 0, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(0, 0, 0, 0.02) 0%, transparent 50%)
    `,
    pointerEvents: "none",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: "440px",
    padding: "48px",
    borderRadius: "12px",
    background: "#ffffff",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e5e7eb",
    position: "relative",
    overflow: "hidden",
  };

  const topBorderStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "#000000",
  };

  const topBorderBreakStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60px",
    height: "4px",
    background: "#ffffff",
    zIndex: 1,
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: "relative",
    marginBottom: "28px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 16px 14px 48px",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#000000",
    backgroundColor: "#fafafa",
    border: "1.5px solid #e5e7eb",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxSizing: "border-box",
  };

  const inputFocusedStyle: React.CSSProperties = {
    ...inputStyle,
    border: "none",
    backgroundColor: "#ffffff",
    boxShadow: "0 0 0 3px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-1px)",
  };

  const iconStyle: React.CSSProperties = {
    position: "absolute",
    left: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "20px",
    height: "20px",
    color: "#6b7280",
    pointerEvents: "none",
  };

  const passwordToggleStyle: React.CSSProperties = {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  };

  return (
    <div style={containerStyle}>
      <div
        style={{
          ...backgroundBeforeStyle,
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0, 0, 0, 0.01) 10px, rgba(0, 0, 0, 0.01) 20px)`,
        }}
      />
      <div style={{ width: "100%", maxWidth: "440px", position: "relative", zIndex: 1 }}>
        <div style={cardStyle}>
          <div style={topBorderStyle} />
          <div style={topBorderBreakStyle} />
          
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "40px" }}>
            <img
              src="/logo/logo-new.png"
              alt="BMC Logo"
              style={{
                width: "140px",
                height: "auto",
                marginBottom: "24px",
                objectFit: "contain",
                filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))",
              }}
            />
            <h1
              style={{
                fontWeight: 600,
                color: "#000000",
                margin: "0 0 8px 0",
                letterSpacing: "0.01em",
                fontSize: "2rem",
                fontFamily: "'Georgia', 'Times New Roman', serif",
              }}
            >
              Welcome
            </h1>
            <div
              style={{
                width: "40px",
                height: "2px",
                background: "#000000",
                marginBottom: "16px",
              }}
            />
            <p
              style={{
                color: "#6b7280",
                textAlign: "center",
                fontSize: "0.95rem",
                fontWeight: 400,
                maxWidth: "320px",
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              Enter your credentials to access your account
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
            <div style={inputWrapperStyle}>
              <svg
                style={iconStyle}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <input
                type="text"
                placeholder="Username"
                value={formData.user_name || ""}
                onChange={(e) => handleChange("user_name", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && formData.password) {
                    handleSave();
                  }
                }}
                onFocus={(e) => {
                  e.target.style.border = "none";
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.1)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onBlur={(e) => {
                  e.target.style.border = "1.5px solid #e5e7eb";
                  e.target.style.backgroundColor = "#fafafa";
                  e.target.style.boxShadow = "none";
                  e.target.style.transform = "translateY(0)";
                }}
                style={inputStyle}
              />
            </div>

            <div style={inputWrapperStyle}>
              <svg
                style={iconStyle}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <input
                  type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={formData.password || ""}
                onChange={(e) => handleChange("password", e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                onFocus={(e) => {
                  e.target.style.border = "none";
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 0, 0, 0.08), 0 2px 12px rgba(0, 0, 0, 0.1)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onBlur={(e) => {
                  e.target.style.border = "1.5px solid #e5e7eb";
                  e.target.style.backgroundColor = "#fafafa";
                  e.target.style.boxShadow = "none";
                  e.target.style.transform = "translateY(0)";
                }}
                style={inputStyle}
              />
              <button
                type="button"
                onClick={handleTogglePassword}
                style={{
                  ...passwordToggleStyle,
                  WebkitTapHighlightColor: "transparent",
                  outline: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#000000";
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.06)";
                  const svg = e.currentTarget.querySelector("svg");
                  if (svg) svg.style.stroke = "#000000";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6b7280";
                  e.currentTarget.style.backgroundColor = "transparent";
                  const svg = e.currentTarget.querySelector("svg");
                  if (svg) svg.style.stroke = "#6b7280";
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.color = "#000000";
                  const svg = e.currentTarget.querySelector("svg");
                  if (svg) svg.style.stroke = "#000000";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.color = "#000000";
                  const svg = e.currentTarget.querySelector("svg");
                  if (svg) svg.style.stroke = "#000000";
                }}
                onFocus={(e) => {
                  e.currentTarget.style.outline = "none";
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" fill="none" stroke="#6b7280" viewBox="0 0 24 24" style={{ transition: "stroke 0.2s ease" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0L3 3m3.29 3.29L3 3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" fill="none" stroke="#6b7280" viewBox="0 0 24 24" style={{ transition: "stroke 0.2s ease" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "-8px" }}>
              <button
                type="button"
                onClick={() => navigate(path.FORGOT_PASSWORD)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#000000";
                  e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.04)";
                  e.currentTarget.style.transform = "translateX(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#6b7280";
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px 24px",
                borderRadius: "8px",
                background: loading ? "#d1d5db" : "#000000",
                color: "#ffffff",
                fontSize: "0.95rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = "#1a1a1a";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.2)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = "#000000";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
              onMouseDown={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
                }
              }}
              onMouseUp={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.2)";
                }
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
